import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { fetchScopePreferences, saveUserPreferences } from '@/api/modules/userPreferences'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { selectOrganizationSettings } from '@/features/settings/settingsSlice'
import { applyBrandTokens, brandCacheKey } from './brand'
import { brandingTokens, NEUTRAL_BRAND, normalizeBranding } from './branding'
import { BrandingScopeContext } from './brandingScopeContext'
import { DEFAULT_THEME, isTheme, ThemeContext, THEMES, THEME_STORAGE_KEY } from './themeContext'

// Re-exported so the theme layer has one public entry point.
export { DEFAULT_THEME, ThemeContext, THEMES, THEME_STORAGE_KEY }

const DARK_QUERY = '(prefers-color-scheme: dark)'
const PREFERENCE_KEY = 'theme'
const PREFERENCE_SCOPE = 'global'

/**
 * The cached branding profile for a tenant, or null. Guarded like the theme
 * read, and re-normalised so a corrupt or stale entry can only ever produce
 * valid tokens.
 *
 * @param {string|number|null} tenantId
 * @returns {Object|null}
 */
function readCachedBranding(tenantId) {
  if (tenantId === null || tenantId === undefined) return null
  try {
    const stored = JSON.parse(localStorage.getItem(brandCacheKey(tenantId)))
    return stored && typeof stored === 'object' ? normalizeBranding(stored) : null
  } catch {
    return null
  }
}

/**
 * @param {string|number} tenantId
 * @param {Object} profile - A normalised branding profile
 * @returns {void}
 */
function writeCachedBranding(tenantId, profile) {
  try {
    localStorage.setItem(brandCacheKey(tenantId), JSON.stringify(profile))
  } catch {
    // Private mode or a full quota: the next load just starts neutral.
  }
}

/**
 * The stored theme, or null. Storage access is guarded: Safari private mode
 * throws on read as well as write.
 *
 * @returns {string|null}
 */
function readStoredTheme() {
  try {
    const stored = JSON.parse(localStorage.getItem(THEME_STORAGE_KEY))
    return isTheme(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * @param {string} theme
 * @returns {void}
 */
function writeStoredTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme))
  } catch {
    // Private mode: the in-memory value still rules for this session.
  }
}

/**
 * @returns {boolean} Whether the OS currently prefers dark
 */
function systemPrefersDark() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(DARK_QUERY).matches
}

/**
 * Loads the saved theme from the server, once, for a signed-in user.
 *
 * Split out so the request is tied to a mount that only happens when
 * authenticated: calling the API on every mount would put a 401 on `/login`
 * and on the public style guide.
 *
 * @param {Object} props
 * @param {(theme: string) => void} props.onLoad - Applies a server value locally
 * @returns {null}
 */
function ThemeSync({ onLoad }) {
  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        // The whole scope, shared with the other global-preference readers.
        const preferences = await fetchScopePreferences(PREFERENCE_SCOPE)
        const preference = preferences.find((entry) => entry.key === PREFERENCE_KEY)

        if (!cancelled && isTheme(preference?.value)) {
          onLoad(preference.value)
        }
      } catch (error) {
        // The local value keeps ruling; nothing the user can act on.
        console.error('Failed to load the theme preference:', error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [onLoad])

  return null
}

/**
 * Owns the theme and the brand colour.
 *
 * Persistence lives here rather than in `useUserPreference` because that hook
 * fires an API call on mount whether or not anyone is signed in. The storage
 * key is the same, so the two never disagree.
 *
 * Must work with `organizationSettings === null` — the style guide is public.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme() ?? DEFAULT_THEME)
  const [systemDark, setSystemDark] = useState(systemPrefersDark)
  const [preview, setPreview] = useState(null)
  // An embedding host's scheme (`useEmbedThemeOverride`): wins over the
  // stored preference and the OS while set, never persisted.
  const [themeOverride, setThemeOverride] = useState(null)
  const [publicScope, setPublicScope] = useState(null)
  // Bumped every time `applyBrandTokens` actually writes to the DOM below,
  // so consumers that read a live CSS custom property (`useCssColorVariable`)
  // can re-read on the DOM write itself rather than on `brand`, which a
  // nested `BrandingScope` can already report as final before this effect
  // has had its later commit (V-E review item 2).
  const [tokensVersion, setTokensVersion] = useState(0)

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const tenantId = useSelector((state) => state.auth?.currentTenantId ?? null)
  const organizationSettings = useSelector(selectOrganizationSettings)
  const contextKey = `${isAuthenticated ? 'member' : 'visitor'}:${tenantId ?? ''}`
  // The preferences API is tenant-scoped: signed in on /verify-email or the
  // tenant picker, there is no workspace yet and it answers 400
  // tenant_required. The stored local theme rules until one is chosen.
  const canSyncPreference = isAuthenticated && tenantId !== null
  // Settings that belong to the signed-in tenant, or null while they are
  // loading, after a failed load, or while the store still holds another
  // tenant's settings mid-switch.
  const tenantProfile = useMemo(() => {
    if (!isAuthenticated || !organizationSettings) return null
    const profile = normalizeBranding(organizationSettings)
    if (profile.tenant_id && String(profile.tenant_id) !== String(tenantId)) return null
    return profile
  }, [isAuthenticated, tenantId, organizationSettings])
  const savedBranding = useMemo(() => {
    if (!isAuthenticated) return normalizeBranding(null)
    if (tenantProfile) return tenantProfile
    // No tenant chosen yet (tenant picker): the product brand is right.
    if (tenantId === null) return normalizeBranding(null)
    // Brand before paint (audit S3): the last brand this tenant resolved to,
    // else a neutral ink, applied by the first layout effect below.
    return readCachedBranding(tenantId) ?? normalizeBranding({ brand_color: NEUTRAL_BRAND })
  }, [isAuthenticated, tenantId, tenantProfile])

  useEffect(() => {
    if (tenantProfile && tenantId !== null) writeCachedBranding(tenantId, tenantProfile)
  }, [tenantId, tenantProfile])

  const resolved = themeOverride ?? (theme === 'system' ? (systemDark ? 'dark' : 'light') : theme)

  // The pre-paint script has already set this for the first render; from here
  // on it follows the preference.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = resolved
  }, [resolved])

  // Only follow the OS while the user has asked us to.
  useEffect(() => {
    if (theme !== 'system') return undefined
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined

    const query = window.matchMedia(DARK_QUERY)
    const handleChange = (event) => setSystemDark(event.matches)

    setSystemDark(query.matches)
    query.addEventListener('change', handleChange)
    return () => query.removeEventListener('change', handleChange)
  }, [theme])

  const branding = useMemo(() => {
    if (publicScope) return normalizeBranding(publicScope.profile)
    return preview?.contextKey === contextKey ? normalizeBranding(preview.profile) : savedBranding
  }, [publicScope, preview, contextKey, savedBranding])
  const tokens = useMemo(() => brandingTokens(branding, resolved), [branding, resolved])

  useLayoutEffect(() => {
    applyBrandTokens(tokens)
    setTokensVersion((version) => version + 1)
    return () => {
      for (const property of Object.keys(tokens)) document.documentElement.style.removeProperty(property)
    }
  }, [tokens])

  // Clear drafts at each identity transition, including switching away and back.
  useEffect(() => { setPreview(null) }, [contextKey])

  const claim = useCallback((owner, profile) => setPublicScope({ owner, profile }), [])
  const release = useCallback((owner) => setPublicScope((current) => current?.owner === owner ? null : current), [])
  const scopeControls = useMemo(() => ({ claim, release }), [claim, release])

  const setTheme = useCallback(
    (next) => {
      if (!isTheme(next)) {
        console.error(`Ignoring unknown theme "${next}"`)
        return
      }

      setThemeState(next)
      writeStoredTheme(next)

      if (!canSyncPreference) return

      saveUserPreferences([{ key: PREFERENCE_KEY, value: next, scope: PREFERENCE_SCOPE }])
        .catch((error) => {
          // The local value stands; the user sees the theme they picked.
          console.error('Failed to save the theme preference:', error)
        })
    },
    [canSyncPreference]
  )

  // Server wins on load. Not routed through setTheme: re-posting a value the
  // server just gave us would be a write for nothing.
  const applyServerTheme = useCallback((next) => {
    setThemeState((current) => {
      if (current === next) return current
      writeStoredTheme(next)
      return next
    })
  }, [])

  // Any empty value clears: a colour input that has been cleared hands back
  // '', which `?? null` would keep as a preview of nothing and resolve to the
  // product default rather than to the tenant's colour.
  const setBrandingPreview = useCallback((next) => {
    setPreview(next ? { contextKey, profile: next } : null)
  }, [contextKey])

  const setBrandPreview = useCallback((next) => {
    setBrandingPreview(next ? { ...savedBranding, brand_color: next } : null)
  }, [savedBranding, setBrandingPreview])

  const value = useMemo(
    () => ({ theme, setTheme, resolved, brand: branding.brand_color, branding, tokensVersion, setBrandPreview, setBrandingPreview, setThemeOverride }),
    [theme, setTheme, resolved, branding, tokensVersion, setBrandPreview, setBrandingPreview]
  )

  return (
    <ThemeContext.Provider value={value}>
      <BrandingScopeContext.Provider value={scopeControls}>
        {canSyncPreference && <ThemeSync onLoad={applyServerTheme} />}
        {children}
      </BrandingScopeContext.Provider>
    </ThemeContext.Provider>
  )
}

export default ThemeProvider
